import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Phone, Mail, Calendar, FileText, CheckSquare, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

const activityTypes = [
  { value: "call", label: "Telefonsamtale", icon: Phone },
  { value: "meeting", label: "Møte", icon: Calendar },
  { value: "email", label: "E-post", icon: Mail },
  { value: "note", label: "Notat", icon: FileText },
  { value: "evaluation", label: "Evaluering", icon: CheckSquare },
  { value: "document", label: "Dokument", icon: FileText },
];

const typeColors = {
  call: "bg-blue-100 text-blue-700",
  meeting: "bg-lime-100 text-zinc-900",
  email: "bg-green-100 text-green-700",
  note: "bg-slate-100 text-slate-700",
  evaluation: "bg-amber-100 text-amber-700",
  document: "bg-lime-100 text-zinc-900",
};

export default function ActivityTimeline({ candidateId, candidateName, activities }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: "note",
    title: "",
    description: "",
    date: new Date().toISOString().slice(0, 16),
    price_discussed: "",
  });
  const queryClient = useQueryClient();

  const createActivityMutation = useMutation({
    mutationFn: async (data) => {
      const user = await base44.auth.me();
      return base44.entities.AcquisitionActivity.create({
        ...data,
        candidate_id: candidateId,
        candidate_name: candidateName,
        user_email: user.email,
        user_name: user.full_name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["acquisition-activities"] });
      setShowForm(false);
      setFormData({
        type: "note",
        title: "",
        description: "",
        date: new Date().toISOString().slice(0, 16),
        price_discussed: "",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createActivityMutation.mutate({
      ...formData,
      price_discussed: formData.price_discussed ? parseFloat(formData.price_discussed) : null,
    });
  };

  const getActivityIcon = (type) => {
    const activityType = activityTypes.find(t => t.value === type);
    return activityType ? activityType.icon : FileText;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Aktivitetslogg</h3>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Ny Aktivitet
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 bg-white border-0 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-slate-900">Registrer Aktivitet</h4>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {activityTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Dato og tid</Label>
                <Input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tittel</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="f.eks. Innledende møte"
              />
            </div>

            <div className="space-y-2">
              <Label>Beskrivelse</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder="Hva ble diskutert?"
              />
            </div>

            <div className="space-y-2">
              <Label>Pris Diskutert (valgfritt)</Label>
              <Input
                type="number"
                value={formData.price_discussed}
                onChange={(e) => setFormData({ ...formData, price_discussed: e.target.value })}
                placeholder="NOK"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Avbryt
              </Button>
              <Button type="submit" disabled={createActivityMutation.isPending}>
                {createActivityMutation.isPending ? "Lagrer..." : "Lagre"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {activities.length === 0 ? (
        <Card className="p-12 text-center bg-white border-0 shadow-sm">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">Ingen aktiviteter registrert</p>
          <Button onClick={() => setShowForm(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Registrer Første Aktivitet
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {activities.map((activity) => {
            const Icon = getActivityIcon(activity.type);
            const typeLabel = activityTypes.find(t => t.value === activity.type)?.label || activity.type;

            return (
              <Card key={activity.id} className="p-6 bg-white border-0 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-900">{activity.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={typeColors[activity.type]}>{typeLabel}</Badge>
                          <span className="text-sm text-slate-500">
                            {format(new Date(activity.date), "dd.MM.yyyy HH:mm")}
                          </span>
                        </div>
                      </div>
                      {activity.price_discussed && (
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Pris diskutert</p>
                          <p className="font-semibold text-emerald-600">
                            {activity.price_discussed.toLocaleString()} kr
                          </p>
                        </div>
                      )}
                    </div>
                    {activity.description && (
                      <p className="text-slate-600 mt-2">{activity.description}</p>
                    )}
                    <p className="text-sm text-slate-500 mt-2">
                      Registrert av {activity.user_name}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}